import React, { useId, useMemo } from 'react';
import Select, { SingleValue, StylesConfig } from 'react-select';
import { MapPin } from 'lucide-react';
import type { Location } from '../types';
import {
  getLocationDisplayName,
  isLocationActive,
  ACTIVE_LOCATION_COLOR,
  INACTIVE_LOCATION_COLOR,
} from '../utils/locationDisplay';

export type LocationSelectOption = {
  value: string;
  label: string;
  isInactive: boolean;
};

const selectStyles: StylesConfig<LocationSelectOption, false> = {
  control: (base, state) => ({
    ...base,
    minHeight: 40,
    borderRadius: 10,
    fontSize: '0.875rem',
    borderColor: state.isFocused ? '#3b82f6' : '#e6eaf0',
    boxShadow: 'none',
    '&:hover': { borderColor: '#cbd5e1' },
    backgroundColor: state.isDisabled ? '#f8fafc' : '#fff',
  }),
  valueContainer: (base) => ({
    ...base,
    paddingLeft: 28,
  }),
  indicatorSeparator: () => ({ display: 'none' }),
  dropdownIndicator: (base) => ({
    ...base,
    color: '#94a3b8',
    paddingLeft: 4,
    paddingRight: 8,
  }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  option: (base, state) => ({
    ...base,
    fontSize: '0.875rem',
    color: state.data.isInactive ? INACTIVE_LOCATION_COLOR : ACTIVE_LOCATION_COLOR,
    backgroundColor: state.isSelected ? '#dbeafe' : state.isFocused ? '#f5f5f5' : '#fff',
  }),
  singleValue: (base, state) => ({
    ...base,
    color: state.data.isInactive ? INACTIVE_LOCATION_COLOR : ACTIVE_LOCATION_COLOR,
  }),
};

interface LocationSelectProps {
  locations: Location[];
  value: number | null;
  onChange: (locationId: number | null) => void;
  disabled?: boolean;
  loading?: boolean;
  refreshing?: boolean;
  allowAllLocations?: boolean;
  className?: string;
  title?: string;
  fullWidth?: boolean;
}

const LocationSelect: React.FC<LocationSelectProps> = ({
  locations,
  value,
  onChange,
  disabled = false,
  loading = false,
  allowAllLocations = true,
  className = '',
  title,
  fullWidth = false,
}) => {
  const inputId = useId();
  const options = useMemo(() => {
    const rows: LocationSelectOption[] = allowAllLocations
      ? [{ value: '', label: 'All Locations', isInactive: false }]
      : [];
    for (const loc of locations) {
      rows.push({
        value: String(loc.id),
        label: getLocationDisplayName(loc),
        isInactive: !isLocationActive(loc),
      });
    }
    // Keep the current selection visible while the list refreshes in the background.
    if (
      value != null &&
      !rows.some((row) => row.value === String(value))
    ) {
      rows.push({
        value: String(value),
        label: `Location #${value}`,
        isInactive: false,
      });
    }
    return rows;
  }, [locations, allowAllLocations, value]);

  const selectedOption = useMemo(() => {
    if (value == null) {
      return options.find((o) => o.value === '') ?? null;
    }
    return options.find((o) => o.value === String(value)) ?? null;
  }, [options, value]);

  const handleChange = (opt: SingleValue<LocationSelectOption>) => {
    if (!opt || opt.value === '') {
      onChange(null);
      return;
    }
    onChange(Number(opt.value));
  };

  const hasKnownSelection = value != null;
  const isDisabled = disabled || (loading && locations.length === 0 && !hasKnownSelection);
  const placeholder =
    loading && locations.length === 0 && !hasKnownSelection
      ? 'Loading locations…'
      : locations.length === 0
        ? 'No locations assigned'
        : 'Select location…';

  return (
    <div className={`flex items-center ${className}`} title={title}>
      <div className={`relative ${fullWidth ? 'w-full' : 'w-44 min-w-[11rem] sm:w-48'}`}>
        <MapPin className="pointer-events-none absolute left-2.5 top-1/2 z-10 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <Select<LocationSelectOption, false>
          inputId={inputId}
          options={options}
          value={selectedOption}
          onChange={handleChange}
          isDisabled={isDisabled}
          isSearchable={locations.length > 6}
          isLoading={loading && locations.length === 0 && !hasKnownSelection}
          placeholder={placeholder}
          menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
          menuPosition="fixed"
          styles={selectStyles}
          classNamePrefix="location-select"
          aria-label="Workplace location"
        />
      </div>
    </div>
  );
};

export default LocationSelect;
